import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import { useLocation, useNavigate } from 'react-router-dom';

import { DocumentDetailIcon } from '@/components/common/DocumentDetailIcon';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FINANCIAL_OVERVIEW_TOUR_CLASSES } from '@/constants/tour/classNames';
import { useSignedUrl } from '@/helpers/storage.helper';
import { useGetFdd } from '@/hooks/fdd/useGetFdd';
import { toast } from '@/hooks/ui/use-toast';
import { useFddStore } from '@/stores/useFddStore';

import { FinancialProjectionTab } from './FinancialProjectionTab';

export const DashboardFinancialOverview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);

  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedTab, setSelectedTab] = useState<string>();

  const { fdd, setFdd } = useFddStore();
  const { url: signedFddUrl } = useSignedUrl(fdd?.url);

  const {
    result: fddResult,
    isError: isFddError,
    isSuccess: isFddSuccess,
    isLoading: isFddLoading,
  } = useGetFdd();

  const handelClickTab = (value: string) => {
    setSelectedTab(value);
    navigate(`/dashboard/financial-overview?tab=${value}`);
  };

  const goToPinPage = () => {
    if (selectedTab && fdd && containerRef.current) {
      const pageIndex = fdd[selectedTab as keyof typeof fdd];
      if (typeof pageIndex === 'number' && pageIndex > 0) {
        const container = containerRef.current;
        const pageHeight = container.scrollHeight / numPages;
        const scrollPosition = (pageIndex - 1) * pageHeight;
        container.scrollTo({
          top: scrollPosition,
          behavior: 'smooth',
        });
        setCurrentPage(pageIndex);
      }
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const newDefaultTab = queryParams.get('tab') || 'franchisee_fee';
    setSelectedTab(newDefaultTab);
  }, [location]);

  useEffect(() => {
    goToPinPage();
  }, [fdd, numPages, selectedTab]);

  useEffect(() => {
    if (isFddError) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later',
        variant: 'destructive',
      });
    }
  }, [isFddError]);

  useEffect(() => {
    if (isFddSuccess && fddResult?.data) {
      setFdd(fddResult.data);
    }
  }, [fddResult, isFddSuccess, setFdd]);

  return (
    <div className="space-y-4">
      <div
        className={`${FINANCIAL_OVERVIEW_TOUR_CLASSES.HEADER} flex flex-col justify-center md:flex-row md:justify-between`}
      >
        <div className="flex flex-col items-center gap-5 md:flex-row">
          <div className="space-y-1">
            <h2 className="as-strong">Financial Overview</h2>
            <p className="as-body-02 as-strong text-typo-gray-tertiary">
              All the financial information will be here
            </p>
          </div>
        </div>
      </div>
      <Tabs
        value={selectedTab}
        defaultValue="franchisee_fee"
        onValueChange={(value) => handelClickTab(value)}
      >
        <TabsList
          className={`${FINANCIAL_OVERVIEW_TOUR_CLASSES.TABS_LIST} no-scroll-bar mb-4 h-auto max-w-full justify-start overflow-x-auto md:flex-wrap`}
        >
          <TabsTrigger
            value="franchisee_fee"
            className={`${FINANCIAL_OVERVIEW_TOUR_CLASSES.TAB_FRANCHISEE_FEE}`}
          >
            Franchisee Fee
          </TabsTrigger>
          <TabsTrigger
            value="initial_investment"
            className={`${FINANCIAL_OVERVIEW_TOUR_CLASSES.TAB_INITIAL_INVESTMENT}`}
          >
            Initial Investment
          </TabsTrigger>
          <TabsTrigger
            value="royalities_brandfund_tech_feeee"
            className={`${FINANCIAL_OVERVIEW_TOUR_CLASSES.TAB_ROYALTIES}`}
          >
            Royalities, Brand Fund, Tech Fee
          </TabsTrigger>
          <TabsTrigger
            value="financial_performance_representation"
            className={`${FINANCIAL_OVERVIEW_TOUR_CLASSES.TAB_PERFORMANCE}`}
          >
            Financial Performance Representation
          </TabsTrigger>
          <TabsTrigger
            value="financial_projection"
            className={`${FINANCIAL_OVERVIEW_TOUR_CLASSES.TAB_PROJECTION}`}
          >
            Financial Projection Workbook
          </TabsTrigger>
        </TabsList>
        {[
          'franchisee_fee',
          'initial_investment',
          'royalities_brandfund_tech_feeee',
          'financial_performance_representation',
        ].includes(selectedTab ?? '') && (
          <div
            className={`${FINANCIAL_OVERVIEW_TOUR_CLASSES.FDD_DOCUMENT_VIEWER} rounded-xl border border-alpha-dark-5 bg-muted md:p-4`}
          >
            <div className="border-b border-border max-md:p-4 md:mb-4 md:pb-4">
              <p className="as-title-01 as-strong">FDD</p>
            </div>

            {isFddLoading ? (
              <div className="flex items-center justify-center">
                <Loader2 className="size-4 animate-spin" />
              </div>
            ) : fdd ? (
              <div className="overflow-hidden bg-white p-3 md:rounded-xl md:border md:border-alpha-dark-5 md:p-2">
                <div className="mb-2 flex items-center justify-between">
                  <div className="ml-4">
                    <DocumentDetailIcon
                      url={fdd.url}
                      name={fdd.name}
                      size={fdd.size}
                    />
                  </div>
                  {numPages > 0 && (
                    <div
                      className={`${FINANCIAL_OVERVIEW_TOUR_CLASSES.FDD_PAGE_INDICATOR} mr-6 text-sm text-gray-500`}
                    >
                      Page {currentPage} of {numPages}
                    </div>
                  )}
                </div>
                <div className="max-md:p-0 md:p-6">
                  <div
                    ref={containerRef}
                    className="h-[800px] overflow-y-auto rounded-xl border"
                    onScroll={(e) => {
                      const container = e.currentTarget;
                      const scrollTop = container.scrollTop;
                      const pageHeight = container.scrollHeight / numPages;
                      const newPage = Math.floor(scrollTop / pageHeight) + 1;
                      setCurrentPage(newPage);
                    }}
                  >
                    <Document
                      file={signedFddUrl ?? null}
                      noData={<Loader2 className="size-4 animate-spin" />}
                      loading={<Loader2 className="size-4 animate-spin" />}
                      className="w-full space-y-4 bg-muted"
                      onLoadSuccess={({ numPages }) => {
                        goToPinPage();
                        setNumPages(numPages);
                      }}
                    >
                      {Array.from(new Array(numPages), (_, index) => (
                        <div
                          key={`page-container-${index}`}
                          className="flex min-h-[800px] items-center justify-center"
                        >
                          <Page
                            key={`page_${index + 1}`}
                            pageNumber={index + 1}
                            width={undefined}
                            className="w-full"
                          />
                        </div>
                      ))}
                    </Document>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <p className="as-body-02 as-strong text-typo-gray-tertiary">
                  No FDD found
                </p>
              </div>
            )}
          </div>
        )}
        <TabsContent
          value="financial_projection"
          className="mt-0 rounded-xl border border-alpha-dark-5 bg-muted p-4"
        >
          <FinancialProjectionTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};
